<template>
  <InfuseTable
    :columns="columns"
    :rows="rows"
    :loading="loading"
    :editable="editable"
    :removable="removable"
    :search="search"
    :has-more-items="false"
    :edit-permission-id="chargingStationPermissions.LINK"
    :remove-permission-id="chargingStationPermissions.LINK"
    @click-row="(row: LinkedCentralSystem) => emit('click-row', row)"
    @load="emit('load')"
    @remove="(row: LinkedCentralSystem) => emit('remove', row)"
    @edit="(row: LinkedCentralSystem) => emit('edit', row)"
    @search="(query: string) => emit('search', query)"
  >
    <template #actions>
      <InfuseButton
        v-if="createEnabled"
        v-permission="{ id: chargingStationPermissions.LINK, hide: false }"
        class="w-64"
        size="medium"
        @click="emit('create')"
      >
        <font-awesome-icon class="mr-1" :icon="['fas', 'link']" />
        {{ t('e_connector.chargingStationLinks.table.create_button') }}
      </InfuseButton>
    </template>

    <template #customChargingStationName="{ data }">
      <div class="italic">{{ data || '-' }}</div>
    </template>

    <template #type="{ data }">
      <InfuseTag v-if="data === LinkedCentralSystemType.MASTER" variant="primary">{{ data }}</InfuseTag>
      <InfuseTag v-else variant="neutral">{{ data }}</InfuseTag>
    </template>

    <template #commandsAllowed="{ data }">
      <font-awesome-icon
        :icon="['fas', data ? 'circle-check' : 'circle-xmark']"
        :class="data ? 'text-primary-500' : 'text-neutral-500'"
      ></font-awesome-icon>
    </template>

    <template #sessionAuthorizationAllowed="{ data }">
      <font-awesome-icon
        :icon="['fas', data ? 'circle-check' : 'circle-xmark']"
        :class="data ? 'text-primary-500' : 'text-neutral-500'"
      ></font-awesome-icon>
    </template>

    <template #forwardBasicAuth="{ data }">
      <font-awesome-icon
        :icon="['fas', data ? 'circle-check' : 'circle-xmark']"
        :class="data ? 'text-primary-500' : 'text-neutral-500'"
      ></font-awesome-icon>
    </template>
  </InfuseTable>
</template>

<script lang="ts" setup>
import { InfuseButton, InfuseTable, InfuseTag } from '@infuse/ui/components';
import { vPermission } from '@infuse/iam/permission/directives';
import type { UiTableColumn } from '@infuse/ui/domain';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { LinkedCentralSystem, LinkedCentralSystemType } from '@infuse/e-connector/central-system/domain';
import { LinkedCentralSystemHydrated } from '@infuse/e-connector/shared/feature';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { chargingStationPermissions } from '@infuse/api/e-connector/charging-station';

interface Emits {
  (e: 'click-row', row: LinkedCentralSystem): void;
  (e: 'create'): void;
  (e: 'load'): void;
  (e: 'remove', row: LinkedCentralSystem): void;
  (e: 'edit', row: LinkedCentralSystem): void;
  (e: 'search', query: string): void;
}

const emit = defineEmits<Emits>();

interface Props {
  createEnabled: boolean;
  editable: boolean;
  loading?: boolean;
  removable?: boolean;
  rows: LinkedCentralSystemHydrated[];
  searchEnabled: boolean;
  searchQuery?: string;
}

const props = withDefaults(defineProps<Props>(), {
  createEnabled: false,
  editable: false,
  loading: false,
  removable: false,
  searchEnabled: true,
  searchQuery: ''
});

const { t } = useI18n();
const search = computed(() =>
  !props.searchEnabled
    ? undefined
    : {
        placeholder: t('e_connector.common.table_search_all'),
        query: props.searchQuery
      }
);

const columns: UiTableColumn[] = [
  {
    name: 'centralSystemName',
    label: () => t('e_connector.common.chargingStationLink.centralSystemName'),
    field: 'centralSystem.name',
    bold: true
  },
  {
    name: 'customChargingStationName',
    label: () => t('e_connector.common.chargingStationLink.customChargingStationName'),
    field: 'customChargingStationName'
  },
  {
    name: 'type',
    label: () => t('e_connector.common.chargingStationLink.type'),
    field: 'type'
  },
  {
    name: 'commandsAllowed',
    label: () => t('e_connector.common.chargingStationLink.commandsAllowed'),
    field: 'commandsAllowed'
  },
  {
    name: 'sessionAuthorizationAllowed',
    label: () => t('e_connector.common.chargingStationLink.authorizationAllowed'),
    field: 'sessionAuthorizationAllowed'
  },
  {
    name: 'forwardBasicAuth',
    label: () => t('e_connector.common.chargingStationLink.forwardBasicAuth'),
    field: 'forwardBasicAuth'
  }
];
</script>
